import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';

/**
 * A menu button's parts (WAI-ARIA menu button), shared by the shell's
 * workspace and account menus and the inbox's filter and tag menus. The panel
 * itself is a `Popover` with `role="menu"`.
 */

export const MENU_ITEM =
  'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] font-medium text-(--color-ink-700) outline-offset-0 transition-colors hover:bg-(--color-accent-wash-soft) hover:text-(--color-ink-900) focus-visible:bg-(--color-accent-wash-soft) disabled:opacity-60 [@media(pointer:coarse)]:min-h-11';

const MENU_ITEMS = '[role^="menuitem"]:not([disabled])';

/**
 * Keyboard model of a menu: focus lands on the first item when it opens,
 * arrows and Home/End move between items, Tab closes it. Escape and outside
 * clicks close it through the Popover; `onClose` hands focus back to the
 * trigger either way.
 */
export function MenuItems({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  // A frame later: the popover is invisible until it has measured itself, and
  // an invisible element cannot take focus.
  useEffect(() => {
    const frame = requestAnimationFrame(() => ref.current?.querySelector<HTMLElement>(MENU_ITEMS)?.focus());
    return () => cancelAnimationFrame(frame);
  }, []);

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const list = Array.from(ref.current?.querySelectorAll<HTMLElement>(MENU_ITEMS) ?? []);
    const index = list.indexOf(document.activeElement as HTMLElement);
    const move = (next: number) => {
      event.preventDefault();
      list[(next + list.length) % list.length]?.focus();
    };
    if (event.key === 'ArrowDown') move(index + 1);
    else if (event.key === 'ArrowUp') move(index - 1);
    else if (event.key === 'Home') move(0);
    else if (event.key === 'End') move(list.length - 1);
    else if (event.key === 'Tab') {
      event.preventDefault();
      onClose();
    }
  };

  return (
    <div ref={ref} onKeyDown={onKeyDown}>
      {children}
    </div>
  );
}

/** Open state of a menu button; closing returns focus to the trigger. */
export function useMenuButton() {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);
  return { triggerRef, open, toggle: () => setOpen((o) => !o), close };
}
